// A pull request's place in its stack, for the pull request page's stack
// menu. The stack is found among a pool of pull requests (`StackPool`: the
// stack another member's page held, the dashboard's saved rows, or what
// the page's own stack read answered) by
// the dashboard's `stackOf`, so it is the stack the dashboard shows: a
// native GitHub stack, or one found by branches. Each member is in the
// state the dashboard's rail shows it in. Pure.
import type { PullRequestData, StackMemberPr, StackRowPr } from '../api/queries';
import { buildsOnText, rail } from './stackRail';
import { checkState, rowReview, type CheckState, type MemberState } from './stackState';
import { memberKey, stackOf, type MissingNode, type PrNode, type Stack, type StackNode } from './stacks';

/** What a pull request's stack is found among. */
export interface StackPool {
  /** Where it was read from: the dashboard's saved copy, or a pull request page's own read of GitHub. */
  source: 'dashboard' | 'github';
  /** Pull requests that may be in the stack; a copy of the page's own is replaced by the page's once that is loaded. */
  prs: StackRowPr[];
  /** The page's pull request's native stack membership, which its own load does not read; nulls outside one. */
  own: Pick<StackRowPr, 'stack' | 'stackEntry'>;
  /** Native stack members GitHub does not show the viewer, as the dashboard learnt reading them by ID (`memberKey`). */
  hidden: string[];
  /** The repository has more open pull requests than were read: a stack found by branches may lack members. */
  cut: boolean;
}

/** A member the menu links to. */
export interface StackMenuPr {
  kind: 'pr';
  /** Its position in the stack, 1 at the base. */
  index: number;
  /** 0 on the stack's line, 1 on a branch off it (two pull requests built on one). */
  lane: 0 | 1;
  /** What it builds on, in words, when that is not the row before it; else null. */
  on: string | null;
  owner: string;
  repo: string;
  number: number;
  title: string;
  state: 'open' | 'draft' | 'merged' | 'closed';
  /** The fill of its node, as on the dashboard's rail (`memberState`, with no attention known). */
  member: MemberState;
  /** GitHub's review decision. */
  review: 'approved' | 'changes' | null;
  /** Its head commit's checks, and GitHub's `statusCheckRollup.state` they come from (null: none). */
  checks: CheckState;
  rollup: string | null;
  /** Its head commit, which the viewer's recorded progress is for; null when GitHub named none. */
  head: string | null;
  /** The pull request the page shows. */
  current: boolean;
}

/** Consecutive members that cannot be shown: GitHub hides them from the viewer, or they were not read. */
export interface StackMenuGap {
  kind: 'gap';
  /** The positions they hold, `index` to `to` inclusive. */
  index: number;
  to: number;
  lane: 0 | 1;
  hidden: boolean;
  text: string;
}

export type StackMenuRow = StackMenuPr | StackMenuGap;

export interface StackMenu {
  /** Pull requests in the stack, shown or not. */
  count: number;
  /** This pull request's position, 1 at the base. */
  position: number;
  /** Base first. */
  rows: StackMenuRow[];
  /** The nearest member this one builds on that the menu links to, and the nearest built on it (where the stack forks, the one that continues it). */
  below: StackMenuPr | null;
  above: StackMenuPr | null;
  /** Found by branches (a base branch that is another open pull request's head), in whole or in part, not GitHub's native stack alone. */
  inferred: boolean;
  /** `StackPool.cut`, for a stack found by branches. */
  cut: boolean;
}

/** The page's own pull request as a stack member: what the menu shows of it follows the page, not the pool's copy. */
export function selfMember(pr: PullRequestData, repository: { nameWithOwner: string; name: string; owner: { login: string } }): StackMemberPr {
  return {
    id: pr.id,
    number: pr.number,
    title: pr.title,
    isDraft: pr.isDraft,
    state: pr.state,
    updatedAt: pr.updatedAt,
    reviewDecision: pr.reviewDecision,
    repository: { nameWithOwner: repository.nameWithOwner, name: repository.name, owner: { login: repository.owner.login } },
    baseRefName: pr.baseRefName,
    headRefName: pr.headRefName,
    headRepository: pr.headRepository ?? null,
    commits: { nodes: pr.commits.nodes.map((n) => ({ commit: { oid: n.commit.oid, statusCheckRollup: n.commit.statusCheckRollup && { state: n.commit.statusCheckRollup.state } } })) },
  };
}

/** The pool's own copy of `repo#number`, for the page whose pull request has not loaded yet; null when it holds none. */
export function copyIn(pool: StackPool, repo: string, number: number): StackMemberPr | null {
  const key = at(repo, number);
  return pool.prs.find((p) => isAt(p, key)) ?? null;
}

interface Row {
  /** A stack's line totals are not shown here, so its members' lines are not read: they count as none. */
  pr: StackRowPr & { additions: 0; deletions: 0 };
  /** Who a member waits on is not read with the stack. */
  attention: null;
}

const row = (pr: StackRowPr): Row => ({ pr: { ...pr, additions: 0, deletions: 0 }, attention: null });
const at = (repo: string, number: number) => memberKey(repo, number);
const isAt = (p: StackRowPr, key: string) => at(p.repository.nameWithOwner, p.number) === key;

const plural = (n: number) => `${n} pull request${n === 1 ? '' : 's'}`;

function gapOf(n: MissingNode<Row>, lane: 0 | 1): StackMenuGap {
  const count = n.to - n.from + 1;
  const named = n.first === null ? plural(count) : n.last === null || n.last === n.first ? `#${n.first}` : `#${n.first} to #${n.last}`;
  return { kind: 'gap', index: n.index, to: n.index + count - 1, lane, hidden: n.hidden, text: n.hidden ? `${plural(count)} GitHub does not show you` : `${named} not loaded` };
}

function prOf(n: PrNode<Row>, lane: 0 | 1, on: string | null, current: boolean): StackMenuPr {
  const p = n.row.pr;
  const review = rowReview({ state: p.state, review: null, decision: p.reviewDecision, turn: 'unknown', context: false });
  const head = p.commits.nodes[0]?.commit;
  return {
    kind: 'pr',
    index: n.index,
    lane,
    on,
    owner: p.repository.owner.login,
    repo: p.repository.name,
    number: p.number,
    title: p.title,
    state: p.state === 'MERGED' ? 'merged' : p.state === 'CLOSED' ? 'closed' : p.isDraft ? 'draft' : 'open',
    member: review.member,
    review: review.pill,
    checks: checkState(head?.statusCheckRollup?.state),
    rollup: head?.statusCheckRollup?.state ?? null,
    head: head?.oid ?? null,
    current,
  };
}

/** The page's pull request among `pool`, and the stack it is in there (see `stackOf`); the stack is null when it is on its own. */
function place(pool: StackPool, self: StackMemberPr, me: string): { own: Row; stack: Stack<Row> | null } {
  const key = at(self.repository.nameWithOwner, self.number);
  const own = row({ ...self, stack: pool.own.stack, stackEntry: pool.own.stackEntry });
  const others = pool.prs.filter((p) => p.id !== self.id && !isAt(p, key)).map(row);
  return { own, stack: stackOf(own, others, me, new Set(pool.hidden)) };
}

/**
 * The stack menu of the pull request `self` among `pool`: every member
 * base first, this one marked, and the ones it builds on and that build on
 * it. The stack and its layout are the dashboard's (`stackOf`, `rail`), so
 * both number a stack alike and keep the places of members that cannot be
 * shown. Null when the pull request is on its own.
 */
export function stackMenu(pool: StackPool, self: StackMemberPr, me: string): StackMenu | null {
  const { own, stack } = place(pool, self, me);
  if (!stack) return null;
  const crossRepo = stack.repos.length > 1;
  const isOwn = (n: StackNode<Row>): n is PrNode<Row> => n.kind === 'pr' && n.row === own;

  const rows: StackMenuRow[] = [];
  for (const r of rail(stack)) {
    const on = r.base && !r.base.above ? buildsOnText(r.base, crossRepo) : null;
    if (r.kind === 'pr') rows.push(prOf(r.node, r.lane, on, isOwn(r.node)));
    else r.members.forEach((m, i) => rows.push(m.kind === 'pr' ? prOf(m, r.lane, i === 0 ? on : null, isOwn(m)) : gapOf(m, r.lane)));
  }
  const shown = new Map(rows.flatMap((r) => (r.kind === 'pr' ? [[r.index, r] as const] : [])));
  const linked = (n: StackNode<Row> | undefined) => (n?.kind === 'pr' ? (shown.get(n.index) ?? null) : null);

  // The way down from the base to this pull request, then on up the stack's own line.
  const path = (n: StackNode<Row>): StackNode<Row>[] | null => {
    if (isOwn(n)) return [n];
    for (const c of n.children) {
      const p = path(c);
      if (p) return [n, ...p];
    }
    return null;
  };
  const down = path(stack.root) ?? [];
  const below = down.slice(0, -1).reverse().map(linked).find((r) => r) ?? null;
  let above: StackMenuPr | null = null;
  for (let n = down.at(-1)?.children.at(-1); n && !above; n = n.children.at(-1)) above = linked(n);

  let inferred = false;
  const walk = (n: StackNode<Row>) => {
    if (n.kind === 'pr' && !n.row.pr.stack) inferred = true;
    n.children.forEach(walk);
  };
  walk(stack.root);
  return { count: stack.count, position: down.at(-1)?.index ?? 1, rows, below, above, inferred, cut: inferred && pool.cut };
}

/**
 * What rows read elsewhere (`source`) say of the pull request
 * `repo#number`: the pool of its stack (the stack's own members alone, its
 * copy among them) when they hold it in one, `alone` when they hold it on
 * its own, null when they do not hold it.
 */
export function poolAmong(prs: readonly StackRowPr[], hidden: readonly string[], repo: string, number: number, source: StackPool['source'], cut = false): StackPool | 'alone' | null {
  const key = at(repo, number);
  const copy = prs.find((p) => isAt(p, key));
  if (!copy) return null;
  const pool: StackPool = { source, prs: [...prs], own: { stack: copy.stack, stackEntry: copy.stackEntry }, hidden: [...hidden], cut };
  const { stack } = place(pool, copy, '');
  if (!stack) return 'alone';
  const members: StackRowPr[] = [copy];
  const walk = (n: StackNode<Row>) => {
    if (n.kind === 'pr' && !isAt(n.row.pr, key)) members.push(n.row.pr);
    n.children.forEach(walk);
  };
  walk(stack.root);
  return { ...pool, prs: members };
}

/** What is said when there is no pull request to go to under (`below`) or over this one. */
export const stepText = (to: 'below' | 'above') => `This is the ${to === 'below' ? 'bottom' : 'top'} of the stack.`;
